import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiFileText,
  FiAlertCircle,
  FiImage,
  FiUser,
  FiMail,
  FiPhone,
  FiMapPin,
  FiSettings,
  FiHash,
  FiCamera,
  FiVideo,
  FiUpload,
  FiX,
  FiCheckCircle,
  FiChevronDown,
} from 'react-icons/fi';
import { IoHardwareChip, IoLocation } from 'react-icons/io5';
import { createTicket, createTicketWithImage, fetchCurrentUser } from '../api/api';
import { useSocket } from '../context/SocketContext';
import { useAuth } from '../context/AuthContext';
import './NewTicket.css';

// Issue categories as specified
const ISSUE_CATEGORIES = [
  { value: 'electrical', label: 'Electrical' },
  { value: 'hydraulic_leakage', label: 'Hydraulic leakage' },
  { value: 'pallet_movement', label: 'Pallet movement (up and down)' },
  { value: 'lock_issue', label: 'Lock issue' },
  { value: 'other', label: 'Other' },
];

const NewTicket = () => {
  const navigate = useNavigate();
  const socket = useSocket();
  const { user } = useAuth();
  
  // Form state with all 8 required fields
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '+91',
    issueCategory: '',
    systemType: '',
    systemNumber: '',
    description: '',
    filedBy: 'me', // 'me' or 'manual' for logged-in users
  });
  
  // Media upload state
  const [photos, setPhotos] = useState([]); // Max 5 photos
  const [video, setVideo] = useState(null); // Max 25MB
  const [videoError, setVideoError] = useState('');
  
  // Location state (auto-captured)
  const [location, setLocation] = useState({ latitude: null, longitude: null, captured: false });
  const [locationError, setLocationError] = useState('');
  
  // User systems (for conditional display)
  const [userSystems, setUserSystems] = useState([]);
  const [loadingUser, setLoadingUser] = useState(false);
  
  // Form status
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [ticketNumber, setTicketNumber] = useState('');

  // Load user data and systems on mount
  useEffect(() => {
    const loadUserData = async () => {
      if (!user?.id) return;
      setLoadingUser(true);
      try {
        const userData = await fetchCurrentUser();
        // Pre-fill user data
        setFormData(prev => {
          const userPhone = userData?.phone || '';
          const formattedPhone = userPhone.startsWith('+91') ? userPhone : userPhone ? `+91${userPhone}` : prev.phone;
          return {
            ...prev,
            fullName: userData?.name || prev.fullName,
            email: userData?.email || prev.email,
            phone: formattedPhone,
          };
        });
        // Load user systems if available
        const systems = userData?.system_types || userData?.systemTypes || [];
        if (systems.length > 0) {
          setUserSystems(systems);
        }
      } catch (err) {
        console.log('Failed to load user data');
      } finally {
        setLoadingUser(false);
      }
    };
    
    loadUserData();
  }, [user]);

  // Capture GPS location
  const captureLocation = useCallback(() => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        setLocationError('Geolocation is not supported');
        resolve(null);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const loc = { 
            latitude: pos.coords.latitude, 
            longitude: pos.coords.longitude,
            captured: true 
          };
          setLocation(loc);
          resolve(loc);
        },
        () => {
          setLocationError('Unable to capture location');
          resolve(null);
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 }
      );
    });
  }, []);

  // Capture location on mount
  useEffect(() => {
    captureLocation();
  }, [captureLocation]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Clear field error when user types
    if (fieldErrors[name]) {
      setFieldErrors({ ...fieldErrors, [name]: '' });
    }
  };

  // Phone validation: +91 prefix, 10+ digits
  const validatePhone = (phone) => {
    const phoneRegex = /^\+91[0-9]{10,}$/;
    return phoneRegex.test(phone);
  };

  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Photo upload handling (Min 1, Max 5)
  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    
    // Validate files
    const validFiles = files.filter(file => {
      const isImage = file.type.startsWith('image/');
      const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB per image
      return isImage && isValidSize;
    });
    
    // Check total count
    if (photos.length + validFiles.length > 5) {
      setError('Maximum 5 photos allowed');
      return;
    }
    
    // Add preview URLs
    const newPhotos = validFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    
    setPhotos([...photos, ...newPhotos]);
    setError('');
  };

  const removePhoto = (index) => {
    const newPhotos = [...photos];
    URL.revokeObjectURL(newPhotos[index].preview);
    newPhotos.splice(index, 1);
    setPhotos(newPhotos);
  };

  // Video upload handling (Max 25MB)
  const handleVideoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate video
    const isVideo = file.type.startsWith('video/');
    const isValidSize = file.size <= 25 * 1024 * 1024; // 25MB
    
    if (!isVideo) {
      setVideoError('Please upload a valid video file');
      return;
    }
    
    if (!isValidSize) {
      setVideoError('Video must be less than 25MB');
      return;
    }
    
    setVideo({
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
    });
    setVideoError('');
  };

  const removeVideo = () => {
    if (video?.preview) {
      URL.revokeObjectURL(video.preview);
    }
    setVideo(null);
  };

  // Form validation
  const validateForm = () => {
    const errors = {};
    
    // Required field validation
    if (!formData.fullName.trim()) errors.fullName = 'Full name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    else if (!validateEmail(formData.email)) errors.email = 'Please enter a valid email';
    
    if (!formData.phone.trim()) errors.phone = 'Phone is required';
    else if (!validatePhone(formData.phone)) errors.phone = 'Phone must start with +91 and have 10 digits';
    
    if (!formData.issueCategory) errors.issueCategory = 'Please select an issue category';
    if (!formData.systemType.trim()) errors.systemType = 'System type is required';
    if (!formData.systemNumber.trim()) errors.systemNumber = 'System number is required';
    else if (formData.systemNumber < 1 || formData.systemNumber > 999) {
      errors.systemNumber = 'System number must be between 1 and 999';
    }
    
    if (!formData.description.trim()) errors.description = 'Please describe your issue';
    
    // Photo validation (Min 1 required)
    if (photos.length === 0) errors.photos = 'At least 1 photo is required';
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      setError('Please fix the errors above');
      return;
    }

    setSubmitting(true);
    try {
      // Ensure location is captured
      let loc = location;
      if (!location.captured) {
        loc = await captureLocation();
      }
      
      // Prepare form data for multipart upload
      const fd = new FormData();
      
      // Core fields that backend expects - match original format
      const title = `${formData.issueCategory} - ${formData.systemType}`;
      const description = `Reported by: ${formData.fullName}\nContact: ${formData.email}, ${formData.phone}\nSystem: ${formData.systemType} #${formData.systemNumber}\n\n${formData.description}`;
      
      fd.append('title', title);
      fd.append('description', description);
      fd.append('category', formData.issueCategory);
      fd.append('priority', 'medium');
      
      // Add user_id if logged in (backend needs this to associate ticket)
      if (user?.id) {
        fd.append('user_id', user.id);
      }
      
      // Add location if captured
      if (loc?.latitude && loc?.longitude) {
        fd.append('latitude', loc.latitude);
        fd.append('longitude', loc.longitude);
      }
      
      // Add first photo as 'image' (backend expects single image field)
      if (photos.length > 0) {
        fd.append('image', photos[0].file);
      }
      
      // Note: Additional photos and video stored in description text for now
      // Backend doesn't support multiple files in this endpoint
      if (photos.length > 1) {
        // Backend doesn't support multiple images in this version
        // Store additional photo count in description
      }
      
      console.log('[NewTicket] Submitting form...');
      // Log form data entries
      for (let [key, value] of fd.entries()) {
        console.log(`[NewTicket] FormData: ${key} = ${value instanceof File ? `File(${value.name})` : value}`);
      }
      const response = await createTicketWithImage(fd);
      console.log('[NewTicket] Response:', response);
      setTicketNumber(response?.ticket?.ticket_number || response?.ticket_number || response?.ticket?.id || response?.id || 'TKT-' + Date.now().toString().slice(-6));
      setSuccess(true);
      
      // Emit socket event
      if (socket) {
        socket.emit('ticket:created', {
          title: formData.fullName,
          category: formData.issueCategory,
          systemType: formData.systemType,
        });
      }
      
    } catch (err) {
      console.error('Ticket creation error:', err);
      setError(err.message || 'Failed to create ticket. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="new-ticket">
        <div className="success-acknowledgment">
          <div className="ack-icon">
            <FiCheckCircle />
          </div>
          <h2>Complaint Filed Successfully!</h2>
          <p className="ack-ticket-number">Ticket Number: <strong>{ticketNumber}</strong></p>
          <p className="ack-message">
            Thank you for reporting the issue. Our engineer will visit your site within 24 hours.
          </p>
          <div className="ack-actions">
            <button className="ack-btn primary" onClick={() => navigate('/user/history')}>
              View My Complaints
            </button>
            <button className="ack-btn secondary" onClick={() => window.location.reload()}>
              File Another Complaint
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="new-ticket">
      {/* Header */}
      <div className="ticket-header-enhanced">
        <div className="ticket-header-title">
          <h1><FiAlertCircle /> File a Complaint</h1>
          <p>Report an issue with your parking system</p>
        </div>
        {/* GPS Status */}
        <div className={`gps-status ${location.captured ? 'captured' : 'pending'}`}>
          <IoLocation />
          {location.captured ? 'Location Captured' : locationError || 'Capturing location...'}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="ticket-form-enhanced">
        {/* Global Error */}
        {error && <div className="error-banner">{error}</div>}

        {/* Complaint Filed By - Conditional for logged-in users */}
        {user?.id && (
          <div className="form-section">
            <div className="form-group">
              <label htmlFor="filedBy">Complaint Filed By *</label>
              <select
                id="filedBy"
                name="filedBy"
                value={formData.filedBy}
                onChange={handleChange}
                className="form-select"
              >
                <option value="me">Me (Logged in as {user?.name || user?.email})</option>
                <option value="manual">Manual Entry (Different person)</option>
              </select>
            </div>
          </div>
        )}

        {/* Section 1: Contact Information */}
        <div className="form-section">
          <h3 className="section-heading">
            <FiUser /> Contact Information
          </h3>
          
          <div className="form-row">
            <div className="form-group half">
              <label htmlFor="fullName">
                <FiUser className="label-icon" />
                Full Name *
              </label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="Enter your full name"
                className={fieldErrors.fullName ? 'error' : ''}
              />
              {fieldErrors.fullName && <span className="field-error">{fieldErrors.fullName}</span>}
            </div>

            <div className="form-group half">
              <label htmlFor="email">
                <FiMail className="label-icon" />
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
                className={fieldErrors.email ? 'error' : ''}
              />
              {fieldErrors.email && <span className="field-error">{fieldErrors.email}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="phone">
              <FiPhone className="label-icon" />
              Phone Number *
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Enter 10-digit number"
              className={fieldErrors.phone ? 'error' : ''}
            />
            {fieldErrors.phone && <span className="field-error">{fieldErrors.phone}</span>}
            <span className="field-hint">Format: +91 followed by 10 digits</span>
          </div>
        </div>

        {/* Section 2: System Information */}
        <div className="form-section">
          <h3 className="section-heading">
            <IoHardwareChip /> System Information
          </h3>

          {/* User Systems Chips - Conditional */}
          {userSystems.length > 0 && (
            <div className="user-systems-section">
              <label>Your Systems:</label>
              <div className="system-chips">
                {userSystems.map((system, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className={`system-chip ${formData.systemType === system ? 'active' : ''}`}
                    onClick={() => setFormData({ ...formData, systemType: system })}
                  >
                    {system}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group half">
              <label htmlFor="issueCategory">
                <FiAlertCircle className="label-icon" />
                Issue Category *
              </label>
              <div className="select-wrapper">
                <select
                  id="issueCategory"
                  name="issueCategory"
                  value={formData.issueCategory}
                  onChange={handleChange}
                  className={fieldErrors.issueCategory ? 'error' : ''}
                >
                  <option value="">Select Category</option>
                  {ISSUE_CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
                <FiChevronDown className="select-arrow" />
              </div>
              {fieldErrors.issueCategory && <span className="field-error">{fieldErrors.issueCategory}</span>}
            </div>

            <div className="form-group half">
              <label htmlFor="systemType">
                <FiSettings className="label-icon" />
                System Type *
              </label>
              <input
                type="text"
                id="systemType"
                name="systemType"
                value={formData.systemType}
                onChange={handleChange}
                placeholder="e.g., Hydraulic Stack"
                className={fieldErrors.systemType ? 'error' : ''}
              />
              {fieldErrors.systemType && <span className="field-error">{fieldErrors.systemType}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="systemNumber">
              <FiHash className="label-icon" />
              System Number (1-999) *
            </label>
            <input
              type="number"
              id="systemNumber"
              name="systemNumber"
              value={formData.systemNumber}
              onChange={handleChange}
              placeholder="Enter system number"
              min="1"
              max="999"
              className={fieldErrors.systemNumber ? 'error' : ''}
            />
            {fieldErrors.systemNumber && <span className="field-error">{fieldErrors.systemNumber}</span>}
          </div>
        </div>

        {/* Section 3: Issue Description */}
        <div className="form-section">
          <h3 className="section-heading">
            <FiFileText /> Describe Your Issue
          </h3>
          
          <div className="form-group">
            <label htmlFor="description">
              Please describe the problem in detail *
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Describe what happened, when it started, and any error messages you see..."
              rows="6"
              className={fieldErrors.description ? 'error' : ''}
            />
            {fieldErrors.description && <span className="field-error">{fieldErrors.description}</span>}
          </div>
        </div>

        {/* Section 4: Media Upload */}
        <div className="form-section">
          <h3 className="section-heading">
            <FiCamera /> Attach Photos & Video
          </h3>
          
          {/* Photo Upload - Min 1, Max 5 */}
          <div className="form-group">
            <label>
              <FiCamera className="label-icon" />
              Photos * (Min 1, Max 5)
            </label>
            
            <div className="photo-upload-section">
              {/* Photo Preview Grid */}
              {photos.length > 0 && (
                <div className="photo-preview-grid">
                  {photos.map((photo, index) => (
                    <div key={index} className="photo-preview-item">
                      <img src={photo.preview} alt={`Preview ${index + 1}`} />
                      <button
                        type="button"
                        className="remove-photo-btn"
                        onClick={() => removePhoto(index)}
                      >
                        <FiX />
                      </button>
                      <span className="photo-number">{index + 1}</span>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add Photo Button */}
              {photos.length < 5 && (
                <div className="upload-buttons">
                  <label className="upload-btn photo-btn">
                    <FiCamera />
                    <span>Take Photo</span>
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handlePhotoUpload}
                      style={{ display: 'none' }}
                    />
                  </label>
                  <label className="upload-btn gallery-btn">
                    <FiImage />
                    <span>Choose from Gallery</span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handlePhotoUpload}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
              )}
            </div>
            
            {fieldErrors.photos && <span className="field-error">{fieldErrors.photos}</span>}
            <span className="field-hint">At least 1 photo required (first photo will be uploaded). You can attach up to 5 for reference.</span>
          </div>

          {/* Video Upload - Optional, Max 25MB */}
          <div className="form-group">
            <label>
              <FiVideo className="label-icon" />
              Video (Optional, Max 25MB)
            </label>
            
            {video ? (
              <div className="video-preview">
                <div className="video-info">
                  <FiVideo className="video-icon" />
                  <span className="video-name">{video.name}</span>
                  <button
                    type="button"
                    className="remove-video-btn"
                    onClick={removeVideo}
                  >
                    <FiX />
                  </button>
                </div>
              </div>
            ) : (
              <label className="upload-btn video-btn">
                <FiUpload />
                <span>Choose Video</span>
                <input
                  type="file"
                  accept="video/*"
                  onChange={handleVideoUpload}
                  style={{ display: 'none' }}
                />
              </label>
            )}
            
            {videoError && <span className="field-error">{videoError}</span>}
            {!video && <span className="field-hint">Max 25MB. Optional but helpful for diagnosis.</span>}
          </div>
        </div>

        {/* Submit Actions */}
        <div className="form-actions-enhanced">
          <button
            type="button"
            className="cancel-btn"
            onClick={() => navigate('/user/dashboard')}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="submit-btn"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span className="spinner" /> Submitting...
              </>
            ) : (
              <>
                <FiCheckCircle /> Submit Complaint
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewTicket;

